import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import type { TicketResponseTargets, TicketSettings } from '@traceio-org/platform-contracts';
import { PageHeading, PageSection } from '@traceio-org/ui';
import { describeWriteFailure, type WriteFailure } from '../../api';
import { AdminPage } from '../../components/AdminPage';
import { AdminProblem } from '../../components/AdminProblem';
import { LoadingBlock, SavedNotice } from '../../components/feedback';
import { SelectField, TextField } from '../../components/fields';
import { DateTimeText, ScrollTable } from '../../components/records';
import { WriteFailureMessage } from '../../components/WriteDialog';
import { useAbilities, useAdmin } from '../../context';
import { useAdminRead } from '../../useAdminRead';
import { PRIORITY_LABELS, describeTarget } from './ticketFormat';

const PRIORITIES = ['urgent', 'high', 'medium', 'low'] as const;
type TargetPriority = (typeof PRIORITIES)[number];
type Unit = 'none' | 'hours' | 'businessDays';

interface Row {
  unit: Unit;
  amount: string;
}
type Form = Record<TargetPriority, Row>;

/** The contract's bounds (ticketResponseTargetSchema): 1 to 720 hours, or 1 to 30 business days. */
const LIMITS: Record<Exclude<Unit, 'none'>, number> = { hours: 720, businessDays: 30 };

function formOf(settings: TicketSettings): Form {
  const rowOf = (priority: TargetPriority): Row => {
    const target = settings.responseTargets[priority];
    if (!target) return { unit: 'none', amount: '' };
    return 'hours' in target
      ? { unit: 'hours', amount: String(target.hours) }
      : { unit: 'businessDays', amount: String(target.businessDays) };
  };
  return { urgent: rowOf('urgent'), high: rowOf('high'), medium: rowOf('medium'), low: rowOf('low') };
}

function targetsOf(form: Form): {
  targets: TicketResponseTargets;
  errors: Partial<Record<TargetPriority, string>>;
} {
  const targets: TicketResponseTargets = {};
  const errors: Partial<Record<TargetPriority, string>> = {};
  for (const priority of PRIORITIES) {
    const row = form[priority];
    if (row.unit === 'none') continue;
    const amount = Number(row.amount);
    const max = LIMITS[row.unit];
    if (!Number.isInteger(amount) || amount < 1 || amount > max) {
      errors[priority] = `Enter a whole number from 1 to ${max}.`;
      continue;
    }
    targets[priority] = row.unit === 'hours' ? { hours: amount } : { businessDays: amount };
  }
  return { targets, errors };
}

/**
 * Response targets (/tickets/settings): per priority, the time to Trace's first reply (hours, or business days Monday
 * to Friday, UTC). A ticket shows "Reply due in…" from them and turns red once breached. Staff with
 * manageTicketSettings (staff.tickets.labels.manage, what the PUT route enforces) change them; changes apply to tickets escalated or re-prioritised from then on.
 */
export function TicketSettingsPage() {
  const { api } = useAdmin();
  const abilities = useAbilities();
  const key = abilities.readTickets ? 'ticket-settings' : null;
  const read = useAdminRead(key, (signal) => api.readTicketSettings(signal));
  const settings = read.dataKey === key && !read.failure ? read.data : null;
  const canManage = abilities.manageTicketSettings;
  // The edits belong to the settings they started from: a newer read (after saving) starts from its own values.
  const [edit, setEdit] = useState<{ from: string; form: Form } | null>(null);
  const form = settings ? (edit?.from === settings.updatedAt ? edit.form : formOf(settings)) : null;
  const [errors, setErrors] = useState<Partial<Record<TargetPriority, string>>>({});
  const [failure, setFailure] = useState<WriteFailure | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const save = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!form || saving) return;
    const { targets, errors: found } = targetsOf(form);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    setSaving(true);
    setFailure(null);
    try {
      await api.updateTicketSettings(targets);
    } catch (error) {
      const described = describeWriteFailure(error);
      if (described.kind !== 'saved-unreadable') {
        setFailure(described);
        setSaving(false);
        return;
      }
    }
    setSaving(false);
    setNotice('Response targets saved. They apply to tickets escalated or re-prioritised from now on.');
    read.retry();
  };

  let body;
  if (!abilities.readTickets) body = <p className="admin-muted">Your staff role does not include tickets.</p>;
  else if (read.failure)
    body = <AdminProblem failure={read.failure} subject="the response targets" onRetry={read.retry} />;
  else if (!settings || !form) body = <LoadingBlock label="Loading the response targets" />;
  else if (!canManage)
    body = (
      <ScrollTable caption="Response targets" className="admin-table">
        <thead>
          <tr>
            <th scope="col">Priority</th>
            <th scope="col">First Trace reply within</th>
          </tr>
        </thead>
        <tbody>
          {PRIORITIES.map((priority) => (
            <tr key={priority}>
              <th scope="row">{PRIORITY_LABELS[priority]}</th>
              <td>{describeTarget(settings.responseTargets[priority])}</td>
            </tr>
          ))}
        </tbody>
      </ScrollTable>
    );
  else
    body = (
      <form className="admin-target-form" onSubmit={(event) => void save(event)} noValidate>
        {failure ? <WriteFailureMessage failure={failure} /> : null}
        {PRIORITIES.map((priority) => {
          const row = form[priority];
          const setRow = (patch: Partial<Row>) =>
            setEdit({ from: settings.updatedAt, form: { ...form, [priority]: { ...row, ...patch } } });
          return (
            <fieldset key={priority} className="admin-target-row">
              <legend className="admin-field__label">{PRIORITY_LABELS[priority]}</legend>
              <SelectField
                label={`${PRIORITY_LABELS[priority]} target`}
                value={row.unit}
                onChange={(unit) => setRow({ unit: unit as Unit })}
                options={[
                  { value: 'none', label: 'No target' },
                  { value: 'hours', label: 'Hours' },
                  { value: 'businessDays', label: 'Business days' },
                ]}
              />
              {row.unit === 'none' ? null : (
                <TextField
                  label={`${PRIORITY_LABELS[priority]} ${row.unit === 'hours' ? 'hours' : 'business days'}`}
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={LIMITS[row.unit]}
                  value={row.amount}
                  onChange={(amount) => setRow({ amount })}
                  error={errors[priority] ?? null}
                  required
                />
              )}
            </fieldset>
          );
        })}
        <div className="admin-dialog-actions">
          <button
            type="submit"
            className="admin-action admin-action--primary"
            aria-disabled={saving || undefined}
            data-busy={saving || undefined}
          >
            Save targets
          </button>
        </div>
      </form>
    );

  return (
    <AdminPage title="Response targets">
      <Link className="admin-back" to="/tickets">
        <span aria-hidden="true">&larr;</span> Tickets
      </Link>
      <PageHeading
        title="Response targets"
        description="How soon Trace replies first, per priority. A ticket turns red once its target is breached."
      />
      <SavedNotice message={notice} />
      <PageSection
        title="First reply"
        description={
          settings ? (
            <>
              Last changed <DateTimeText iso={settings.updatedAt} />.
            </>
          ) : undefined
        }
      >
        {body}
      </PageSection>
    </AdminPage>
  );
}
